import api from "@/utils/api";
import { saveData, loadData, removeData } from "@/utils/storageHelper";
import { v4 as uuidv4 } from 'uuid';

const getDefaultState = () => ({
  activeCount: 0,
  token: null,
  refreshToken: null,
  sessionType: null,
  user: null,
  locations: [],
  terminals: [],
  config: null,
  summary: null,
  activeLocationId: null,
  initialized: false,
  accessList: [],
});

export default {
  namespaced: true,
  state: () => {
    const savedState = loadData('admin_session');
    if (savedState) {
      const { token, user, activeLocationId, accessList } = JSON.parse(savedState);
      return { ...getDefaultState(), token, user, activeLocationId, accessList };
    }
    return getDefaultState();
  },
  getters: {
    initialized: state => state.initialized,
    isLoggedIn: state => !!state.token,
    isAdmin: state => state.user?.shift4_admin || false,
    userLocations: state => state.locations,
    currentConfig: state => state.config,
    activeLocation: state => state.activeLocation,
    terminals: state => state.terminals,
    summary: state => state.summary,
    locations: state => state.locations,
    accessList: state => state.accessList,
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_REFRESH_TOKEN(state, refreshToken) {
      state.refreshToken = refreshToken;
    },
    SET_SESSION_TYPE(state, sessionType) {
      state.sessionType = sessionType;
    },
    SET_USER(state, user) {
      state.user = user;
    },
    SET_INITIALIZED(state, initialized) {
      state.initialized = initialized;
    },
    SET_LOCATIONS(state, locations) {
      state.locations = locations;
    },
    SET_TERMINALS(state, terminals) {
      state.terminals = terminals;
    },
    SET_ACTIVE_LOCATION_ID(state, locationId) {
      state.activeLocationId = locationId;
    },
    SET_ACTIVE_LOCATION(state, location) {
      state.activeLocation = location;
    },
    SET_CONFIG(state, config) {
      state.config = config;
    },
    SET_SUMMARY(state, summary) {
      state.summary = summary;
    },
    CLEAR_STATE(state) {
      Object.assign(state, getDefaultState());
    },
    ADD_ORDER_TYPE(state, orderType) {
      state.config.orderTypes.push(orderType);
    },
    UPDATE_ORDER_TYPE(state, { index, orderType }) {
      if (index >= 0 && index < state.config.orderTypes.length) {
        state.config.orderTypes.splice(index, 1, orderType);
      }
    },
    DELETE_ORDER_TYPE(state, index) {
      if (index >= 0 && index < state.config.orderTypes.length) {
        state.config.orderTypes.splice(index, 1);
      }
    },
    SET_ACTIVE_COUNT(state, count) {
      state.activeCount = count;
    },
    SET_ACCESS_LIST(state, accessList) { // Add mutation for accessList
      state.accessList = accessList;
    },
  },
  actions: {
    activeCount({ commit }, count) {
      commit('SET_ACTIVE_COUNT', count);
    },
    async generateSchedules({ state }, plainText) {
      if (!state.activeLocationId) {
        throw new Error("Active location ID is not set.");
      }
      console.log("Generating schedules from text:", plainText);
      const response = await api.callAPI('post', `/admin/schedulesFromText/${state.activeLocationId}`, { text: plainText }, state.token);
      return response || [];
    },
    async login({ commit, dispatch }, credentials) {
      try {
        const { token, user, accessList } = await api.callAPI('post', '/auth/login', credentials);
        commit('SET_TOKEN', token);
        commit('SET_USER', user);
        commit('SET_ACCESS_LIST', accessList); // This is already set correctly
        console.log("Logged in as:", JSON.stringify(user));
        commit('SET_SESSION_TYPE', 'email');
        const activeLocationId = user.locations[0];
        commit('SET_ACTIVE_LOCATION_ID', activeLocationId);
        commit('SET_ACTIVE_LOCATION', user.locations.find(l => l.id === activeLocationId));
        saveData('admin_session', JSON.stringify({ token, user, activeLocationId, accessList })); // Add accessList here
        await dispatch('fetchInitialData');
        return true;
      } catch (error) {
        console.error("Login failed:", error);
        return false;
      }
    },
    async register({ commit, dispatch }, credentials) {
      try {
        const { token, user, accessList } = await api.callAPI('post', '/auth/register', credentials);
        commit('SET_TOKEN', token);
        commit('SET_USER', user);
        commit('SET_ACCESS_LIST', accessList); // This is already set correctly
        console.log("Registered as:", JSON.stringify(user));
        commit('SET_SESSION_TYPE', 'email');
        const activeLocationId = user.locations[0];
        commit('SET_ACTIVE_LOCATION_ID', activeLocationId);
        commit('SET_ACTIVE_LOCATION', user.locations.find(l => l.id === activeLocationId));
        saveData('admin_session', JSON.stringify({ token, user, activeLocationId, accessList })); // Add accessList here
        await dispatch('fetchInitialData');
        return true;
      } catch (error) {
        console.error("Registration failed:", error);
        return false;
      }
    },
    async loginWithSkyTab({ commit, dispatch }, { code }) {
      try {
        // Get tokens
        const { access_token, refresh_token } = await api.callAPI('post', '/auth/lighthouse/callback', { code });
        commit('SET_TOKEN', access_token);
        commit('SET_REFRESH_TOKEN', refresh_token);
        commit('SET_SESSION_TYPE', 'skytab');
        // Get user details with the access token
        //const user = await api.callAPI('get', '/auth/user', null, access_token);
        //commit('SET_USER', user);

        // Fetch locations
        const locations = await api.callAPI('get', '/auth/user/locations', null, access_token);
        commit('SET_LOCATIONS', locations);

        // Set the first location as the active location
        const activeLocationId = locations[0].id;
        commit('SET_ACTIVE_LOCATION_ID', activeLocationId);

        saveData('admin_session', JSON.stringify({ token: access_token, user: null, activeLocationId }));
        await dispatch('fetchInitialData');
        commit('SET_INITIALIZED', true);
        return true;
      } catch (error) {
        console.error("SkyTab login failed:", error);
        return false;
      }
    },
    async getLocations({ commit, state }) {
      if (state.token) {
        const locations = await api.callAPI('get', '/locations', null, state.token);
        commit('SET_LOCATIONS', locations);
      }
    },
    async initiateSkyTabLogin() {
      try {
        const response = await api.callAPI('get', '/auth/lighthouse');
        window.location.href = response;
      } catch (error) {
        console.error("Failed to initiate OAuth login:", error);
      }
    },
    async setActiveLocation({ commit, rootState, dispatch }, locationId) {
      commit('SET_ACTIVE_LOCATION_ID', locationId);
      await dispatch('initWebSocket', { locationId, terminalId: rootState.terminal.id }, { root: true }); // Dispatching globally
      await dispatch('fetchInitialData');
    },
    async fetchInitialData({ state, dispatch, commit }) {
      if (state.token && state.activeLocationId) {
        try {
          await dispatch('fetchConfig');
          await dispatch('fetchLocations');
          await dispatch('fetchTerminals');
          await dispatch('fetchSummary', { period: 'day' });
          commit('SET_ACTIVE_LOCATION', state.locations.find(l => l.id === state.activeLocationId));
          commit('SET_INITIALIZED', true);
        } catch (error) {
          console.error("Failed to fetch initial data:", error);
        }
      }
    },
    async fetchSummary({ commit, state }, { period = 'day' }) {
      if (state.token && state.activeLocationId) {
        const summary = await api.callAPI('get', `/admin/summary/${state.activeLocationId}?period=${period}`, null, state.token);
        commit('SET_SUMMARY', summary);
      }
    },
    async fetchTerminals({ commit, state }) {
      if (state.token && state.activeLocationId) {
        const terminals = await api.callAPI('get', `/admin/terminals/${state.activeLocationId}`, null, state.token);
        commit('SET_TERMINALS', terminals);
      }
    },
    async fetchLocations({ commit, state }) {
      if (state.token && state.activeLocationId) {
        const locations = await api.callAPI('get', '/auth/user/locations', null, state.token);
        commit('SET_LOCATIONS', locations);
      }
    },
    async fetchConfig({ commit, state }) {
      if (state.token && state.activeLocationId) {
        const config = await api.callAPI('get', `/admin/config/${state.activeLocationId}`, null, state.token);
        commit('SET_CONFIG', config);
      }
    },
    logout({ commit }) {
      removeData('admin_session');
      commit('CLEAR_STATE');
    },
    updateActiveLocation({ commit, state }, location) {
      commit('SET_ACTIVE_LOCATION', location);
      saveData('admin_session', JSON.stringify({
        token: state.token,
        activeLocation: location
      }));
    },
    async saveConfig({ state, commit }, config) {
      try {
        const resp = await api.callAPI('post', `/admin/config/${state.activeLocationId}`, config, state.token);
        console.log(resp);
        commit('SET_CONFIG', config);
        return true;
      } catch (error) {
        alert("Failed to save config: " + error.response?.data || error?.message || 'Unknown Error');
        return false
      }
    },
    async uploadLogo({ state }, formData) {
      const response = await api.callAPI('post', `/admin/upload-logo`, formData, state.token);
      return response;
    },
    async deleteLogo({ state, commit }, { url }) {
      await api.callAPI('post', `/admin/delete-logo`, { url }, state.token);
      commit('SET_CONFIG', { ...state.config, logo: { url: null } });
    },
    async updateOrderType({ commit, state, dispatch }, { index, orderType }) {
      if (!orderType.id) {
        orderType.id = uuidv4();
      }
      commit('UPDATE_ORDER_TYPE', { index, orderType });
      const resp = await dispatch('saveConfig', state.config);
      return resp;
    },
    async addOrderType({ commit, state, dispatch }, orderType) {
      if (state.token) {
        if (!orderType.id) {
          orderType.id = uuidv4();
        }
        commit('ADD_ORDER_TYPE', orderType);
        const resp = await dispatch('saveConfig', state.config);
        return resp;
      }
    },
    async deleteOrderType({ commit, state, dispatch }, { index }) {
      if (state.token) {
        const origState = JSON.parse(JSON.stringify(state.config));
        commit('DELETE_ORDER_TYPE', index);
        const resp = await dispatch('saveConfig', state.config);
        if(!resp) {
          commit('SET_CONFIG', origState);
        }
      }
    },
    async deleteTerminal({ state, dispatch }, terminalId) {
      if (state.token) {
        await api.callAPI('delete', `/admin/terminals/${state.activeLocationId}/${terminalId}`, null, state.token);
        dispatch('fetchTerminals');  // Fetch updated list
      }
    },
    async addTerminal({ state, dispatch }, terminalData) {
      const req = {
        locationId: state.activeLocationId,
        name: terminalData.name,
      };
      if (state.token) {
        await api.callAPI('post', `/admin/terminals`, req, state.token);
        dispatch('fetchTerminals');  // Fetch updated list
      }
    },
    async restartTerminals({ state }) {
      if (state.token && state.activeLocationId) {
        await api.callAPI('post', '/admin/restart-terminals', { locationId: state.activeLocationId }, state.token);
      }
    }
  }
};
